/**
 * src/shell/portal/components/MediaShelf.tsx
 *
 * Horizontal scrollable media shelf for the Netflix-style discovery canvas.
 */

import React from 'react';
import { ChevronRight } from 'lucide-react';
import { ScrollableCardRow } from './ScrollableCardRow';

export interface ShelfItem {
  id: string;
  title: string;
  subtitle: string;
  badgeText?: string;
  badgeColor?: string;
  emojiIcon?: string;
  extraMeta?: string;
  onClick?: () => void;
}

export interface MediaShelfProps {
  title: string;
  icon?: string;
  items: ShelfItem[];
  onViewAll?: () => void;
  isCollapsed?: boolean;
  isPinnedToTop?: boolean;
  onToggleCollapse?: () => void;
  onTogglePin?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

export const MediaShelf: React.FC<MediaShelfProps> = ({
  title,
  icon,
  items,
  onViewAll,
  isCollapsed = false,
  isPinnedToTop = false,
  onToggleCollapse,
  onTogglePin,
  onMoveUp,
  onMoveDown,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Reorder Grip / Move Controls */}
          {(onMoveUp || onMoveDown) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginRight: 2 }}>
              <span style={{ color: '#64748b', fontSize: 13, cursor: 'grab' }} title="Drag / Reorder Shelf">
                ⠿
              </span>
              {onMoveUp && (
                <button
                  type="button"
                  onClick={onMoveUp}
                  title="Move Shelf Up"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    fontSize: 11,
                    padding: '2px 4px',
                  }}
                >
                  ▲
                </button>
              )}
              {onMoveDown && (
                <button
                  type="button"
                  onClick={onMoveDown}
                  title="Move Shelf Down"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    fontSize: 11,
                    padding: '2px 4px',
                  }}
                >
                  ▼
                </button>
              )}
            </div>
          )}

          {icon && <span style={{ fontSize: 18 }}>{icon}</span>}
          <span style={{ fontWeight: 800, fontSize: 16, color: '#f8fafc', letterSpacing: 0.2 }}>
            {title}
          </span>
          {isPinnedToTop && (
            <span
              style={{
                fontSize: 10,
                color: '#38bdf8',
                background: 'rgba(56, 189, 248, 0.15)',
                padding: '2px 6px',
                borderRadius: 4,
                fontWeight: 700,
              }}
            >
              PINNED
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {onTogglePin && (
            <button
              type="button"
              onClick={onTogglePin}
              title={isPinnedToTop ? 'Unpin Shelf' : 'Pin Shelf to Top'}
              style={{
                background: isPinnedToTop ? 'rgba(56, 189, 248, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: 'none',
                borderRadius: 6,
                padding: '4px 8px',
                color: isPinnedToTop ? '#38bdf8' : '#94a3b8',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              📌 {isPinnedToTop ? 'Pinned' : 'Pin'}
            </button>
          )}

          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              title={isCollapsed ? 'Expand Shelf' : 'Collapse Shelf'}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: 'none',
                borderRadius: 6,
                padding: '4px 8px',
                color: '#94a3b8',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {isCollapsed ? '▸ Expand' : '▾ Collapse'}
            </button>
          )}

          {onViewAll && !isCollapsed && (
            <button
              type="button"
              onClick={onViewAll}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#38bdf8',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              Explore <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>

      {!isCollapsed && (
        <ScrollableCardRow gap={12}>
          {items.map((item) => (
            <div
              key={item.id}
              onClick={item.onClick}
              style={{
                minWidth: 210,
                maxWidth: 230,
                background: 'rgba(13, 17, 23, 0.75)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: 10,
                padding: '12px 14px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'transform 0.15s ease, border-color 0.15s ease, background-color 0.15s ease',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.4)';
                e.currentTarget.style.backgroundColor = 'rgba(17, 24, 39, 0.85)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.backgroundColor = 'rgba(13, 17, 23, 0.75)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 24 }}>{item.emojiIcon || '🌐'}</span>
                  {item.badgeText && (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        background: item.badgeColor ? `${item.badgeColor}18` : 'rgba(56, 189, 248, 0.12)',
                        color: item.badgeColor || '#38bdf8',
                        border: `1px solid ${item.badgeColor ? `${item.badgeColor}40` : 'rgba(56, 189, 248, 0.25)'}`,
                        padding: '1px 6px',
                        borderRadius: 4,
                        letterSpacing: '0.04em',
                      }}
                    >
                      {item.badgeText}
                    </span>
                  )}
                </div>

                <div style={{ fontWeight: 700, fontSize: 13, color: '#f8fafc', lineHeight: 1.3 }}>
                  {item.title}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, lineHeight: 1.3 }}>
                  {item.subtitle}
                </div>
              </div>

              {item.extraMeta && (
                <div style={{ marginTop: 10, borderTop: '1px solid rgba(255, 255, 255, 0.06)', paddingTop: 6, fontSize: 10, fontFamily: 'monospace', color: '#38bdf8', fontWeight: 600 }}>
                  {item.extraMeta}
                </div>
              )}
            </div>
          ))}
        </ScrollableCardRow>
      )}
    </div>
  );
};
